<script setup lang="ts">
import { Dropdown } from 'bootstrap';
import type { UserNotification } from '~/composables/useNotifications';

/**
 * Bell in the top bar: an unread dot plus a dropdown panel listing the user's
 * notifications, grouped into unread and read.
 */
const { t } = useI18n();
const { notifications, unread, fetchNotifications, markAllAsRead } =
  useNotifications();

// The unread row the user is currently reading. It is held in the unread group even
// though it just turned read, so it does not jump away under the cursor; clicking
// another one hands the hold over and lets the previous row drop into read.
const heldId = ref<string | null>(null);

const hold = (notification: UserNotification) =>
  (heldId.value = notification.read_at ? null : notification.id);

const unreadGroup = computed(() =>
  notifications.value.filter(item => !item.read_at || item.id === heldId.value)
);
const readGroup = computed(() =>
  notifications.value.filter(item => item.read_at && item.id !== heldId.value)
);

onMounted(() => {
  const element = document.getElementById('dropdownLayoutPartsNotifications');
  if (element) {
    new Dropdown(element);
    // Re-read on close so the panel and the dot reflect what the server stored.
    element.addEventListener('hidden.bs.dropdown', () => {
      heldId.value = null;
      fetchNotifications();
    });
  }

  fetchNotifications();
});
</script>

<template>
  <div
    class="dropdown dropdown__background dropdown__no-lines position-relative"
  >
    <button
      id="dropdownLayoutPartsNotifications"
      class="button button__lines-grey d-flex align-items-center"
      data-bs-toggle="dropdown"
      data-bs-auto-close="outside"
      aria-expanded="false"
      :title="t('notifications.title', 2)"
      :aria-label="t('notifications.title', 2)"
    >
      <span class="mx-auto" style="width: 23px">
        <span style="height: 23px; border-radius: 50%">
          <i class="fas fa-bell" />
        </span>
      </span>
    </button>
    <span v-if="unread.length > 0" class="notification-dot" />
    <ul
      aria-labelledby="dropdownLayoutPartsNotifications"
      class="dropdown-menu dropdown-menu-end dropdown-menu-notification"
    >
      <li v-if="notifications.length === 0" class="list-empty text-muted">
        {{ t('notifications.list_empty') }}
      </li>
      <template v-else>
        <li
          class="list-group-title d-flex justify-content-between align-items-center px-3"
        >
          <span>{{ t('notifications.list.unread.title') }}</span>
          <PortalButton
            v-if="unread.length > 0"
            class="btn-sm btn-outline-secondary mark-all-read"
            :action="markAllAsRead"
          >
            {{ t('notifications.buttons.mark_all_read') }}
          </PortalButton>
        </li>
        <li v-if="unreadGroup.length === 0" class="list-empty text-muted">
          {{ t('notifications.list.unread.description') }}
        </li>
        <LayoutPartsNotificationRow
          v-for="notification in unreadGroup"
          :key="notification.id"
          :notification="notification"
          @select="hold(notification)"
        />
        <li v-if="readGroup.length > 0" class="list-group-title px-3">
          {{ t('notifications.list.read.title') }}
        </li>
        <LayoutPartsNotificationRow
          v-for="notification in readGroup"
          :key="notification.id"
          :notification="notification"
          @select="hold(notification)"
        />
      </template>
    </ul>
  </div>
</template>

<style scoped>
/* The shared panel style is a fixed 400px column built for a scroll viewport; this
   panel is a plain list, so let it grow with its content up to that height. Rows wrap
   to the panel width, so nothing is clipped by hiding the horizontal axis — without
   it, overflow-y alone makes the browser compute overflow-x as auto and show a
   horizontal scrollbar. */
/* The shared .list-empty and .list-group-title rules live under the scroll-viewport
   markup this panel does not use, so they are restated here: without them the text
   sits against the panel border and the two groups look identical. */
.list-empty {
  padding: 0.5rem 1rem;
}

.list-group-title {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 13px;
  font-weight: 600;
  background-color: var(--card-bg-grey);
}

/* Sits inside the 13px heading strip, so keep it compact. */
.mark-all-read {
  padding: 0.15rem 0.5rem;
  font-size: 12px;
  font-weight: 400;
  background-color: #fff;
}

.dropdown-menu-notification {
  /* A little wider than the shared 300px so the remove control is not flush against
     the edge. */
  width: 340px;
  /* The dropdown's own top padding shows as a white strip above the first group
     heading; the shared style already drops the bottom one. */
  padding-top: 0;
  height: auto;
  max-height: 400px;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
